import type { ExecutionSummaryV2 } from '@flux/types';

export function assertNever(x: never): never {
  throw new Error(`Unexpected value: ${JSON.stringify(x)}`);
}

/**
 * Derives the active timeline mark for an execution mathematically.
 * Enforces union subsets dynamically without null fallbacks.
 */
export function getExecutionTimestamp(e: ExecutionSummaryV2): string {
  switch (e.status) {
    case 'running':
      return e.receivedAt ?? Date.now().toString(); // Edge might lag hydration during optimistic UI loops
    case 'success':
    case 'error':
      return e.completedAt;
    default:
      return assertNever(e);
  }
}

/**
 * Mathematically derives the duration scalar for an execution object explicitly.
 * Running state carries naturally 0 duration dynamically.
 */
export function getExecutionDuration(e: ExecutionSummaryV2): number {
  switch (e.status) {
    case 'running':
      return 0; // Running state mathematically incurs 0 completed scalar.
    case 'success':
    case 'error':
      return e.durationMs;
    default:
      return assertNever(e);
  }
}

/**
 * Yields Tailwind color classes bound stringently to union boundaries.
 */
export function getExecutionStatusColor(e: ExecutionSummaryV2): string {
  switch (e.status) {
    case 'running':
      return 'text-[var(--flux-accent)]';
    case 'success':
      return 'text-[var(--flux-success)]';
    case 'error':
      return 'text-[var(--flux-error)]';
    default:
      return assertNever(e);
  }
}
